import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Keeps variant-scoped facts and variant-scoped media in sync with the selected variant.
 *
 * Horizon updates price and inventory client-side rather than re-rendering the section, so a
 * server-rendered value keyed to the selected variant would show the first variant's figures
 * for the rest of the session. The fix used throughout this theme is to put every variant's
 * value in the markup and toggle which one is visible: instant, no round trip, and never a
 * moment where one variant's price sits beside another's battery capacity.
 *
 * This generalises the pattern that `variant-specs.js` applied to one block. Any element can
 * opt in by carrying `data-variant-fact="<variant title>"`, so the buy box, quick specs,
 * sticky bar, final CTA and gallery all stay correct through one component rather than four
 * near-identical ones.
 *
 * Media switching resolves through `kewelo.variant_media`, matched on selected option values
 * rather than variant title, so a future Color x Battery product works unchanged.
 *
 * @extends {Component}
 */
class VariantFactComponent extends Component {
  #lastKey = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.productSelect, this.#update);
    // Variant pickers are radio inputs. Listening to change directly covers pickers that do
    // not dispatch the standard event, and costs nothing when they do.
    document.addEventListener('change', this.#update);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.productSelect, this.#update);
    document.removeEventListener('change', this.#update);
  }

  /**
   * Selected option values, in picker order.
   *
   * Read from the picker rather than from an event payload so the result does not depend on
   * the shape of a detail object, and is correct for single- and multi-option products alike.
   *
   * @returns {string[]}
   */
  #selectedOptions() {
    const picker = document.querySelector('variant-picker');
    if (!picker) return [];
    return [...picker.querySelectorAll('input:checked, select')]
      .map((el) => /** @type {HTMLInputElement|HTMLSelectElement} */ (el).value.trim())
      .filter(Boolean);
  }

  /** Shopify joins option values with " / " to form the variant title. */
  #selectedTitle() {
    const values = this.#selectedOptions();
    return values.length ? values.join(' / ') : null;
  }

  /**
   * A quantity change must never move the gallery, and neither must a re-render that did not
   * actually change the selection. Bailing on an unchanged key is what guarantees both.
   */
  #update = () => {
    const title = this.#selectedTitle();
    if (!title || title === this.#lastKey) return;
    this.#lastKey = title;

    for (const el of this.querySelectorAll('[data-variant-fact]')) {
      const match = el.getAttribute('data-variant-fact') === title;
      el.hidden = !match;
    }

    this.dispatchEvent(
      new CustomEvent('variant-fact:change', {
        bubbles: true,
        detail: { title, options: this.#selectedOptions() },
      })
    );
  };
}

if (!customElements.get('variant-fact')) {
  customElements.define('variant-fact', VariantFactComponent);
}
